import React, {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    Skeleton,
    styled,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Card,
    CardContent,
    CardActions,
    Chip,
    CircularProgress,
    Snackbar,
    LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GetAppIcon from '@mui/icons-material/GetApp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {useDispatch, useSelector} from "react-redux";
import {createDailyTask, deleteDailyTask, fetchDailyTask, updateDailyTask,} from "./redux/DailyTaskActions";
import DailyTaskList from "./DailyTaskList";
import DailyTaskForm from "./DailyTaskForm";
import * as XLSX from "xlsx";
import ClearIcon from "@mui/icons-material/Clear";
import {selectSchoolDetails} from "../../../common";

// Enhanced Document Download Utility Functions
const detectFileTypeFromBase64 = (base64String) => {
    if (!base64String) return { type: 'unknown', extension: 'bin', mimeType: 'application/octet-stream' };

    const cleanBase64 = base64String.replace(/^data:[^;]+;base64,/, '');

    try {
        const binary = atob(cleanBase64.substring(0, 50));
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }

        // Check file signatures
        if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
            return { type: 'image', extension: 'jpg', mimeType: 'image/jpeg' };
        }
        if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
            return { type: 'image', extension: 'png', mimeType: 'image/png' };
        }
        if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
            return { type: 'image', extension: 'gif', mimeType: 'image/gif' };
        }
        if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
            return { type: 'pdf', extension: 'pdf', mimeType: 'application/pdf' };
        }
        if (bytes[0] === 0x50 && bytes[1] === 0x4B) {
            const zipString = binary.substring(0, 100);
            if (zipString.includes('word/')) {
                return { type: 'document', extension: 'docx', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' };
            }
            if (zipString.includes('xl/')) {
                return { type: 'document', extension: 'xlsx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' };
            }
            if (zipString.includes('ppt/')) {
                return { type: 'document', extension: 'pptx', mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' };
            }
            return { type: 'document', extension: 'zip', mimeType: 'application/zip' };
        }
        if (bytes[0] === 0xD0 && bytes[1] === 0xCF && bytes[2] === 0x11 && bytes[3] === 0xE0) {
            return { type: 'document', extension: 'doc', mimeType: 'application/msword' };
        }
        // Additional file type detection
        if (bytes[0] === 0x7B || (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF)) {
            return { type: 'document', extension: 'json', mimeType: 'application/json' };
        }
        // Plain text detection (fallback)
        let isText = true;
        for (let i = 0; i < Math.min(bytes.length, 1000); i++) {
            if (bytes[i] === 0 || (bytes[i] < 32 && bytes[i] !== 9 && bytes[i] !== 10 && bytes[i] !== 13)) {
                isText = false;
                break;
            }
        }
        if (isText) {
            return { type: 'document', extension: 'txt', mimeType: 'text/plain' };
        }
    } catch (error) {
        console.warn('Failed to detect file type from base64:', error);
    }

    return { type: 'document', extension: 'bin', mimeType: 'application/octet-stream' };
};

const generateFileName = (originalFileName, dailyTask, extension) => {
    const sanitize = (str) => str ? str.replace(/[^a-zA-Z0-9\-_\s]/g, '').trim() : '';

    let fileName = '';

    if (originalFileName && originalFileName !== '' && originalFileName !== 'undefined') {
        fileName = originalFileName;
        if (!fileName.includes('.')) {
            fileName += `.${extension}`;
        }
    } else {
        const parts = [];

        if (dailyTask?.type) parts.push(sanitize(dailyTask.type));
        if (dailyTask?.subject) parts.push(sanitize(dailyTask.subject));
        if (dailyTask?.title) parts.push(sanitize(dailyTask.title));
        else parts.push('Document');
        if (dailyTask?.className) parts.push(`Class-${sanitize(dailyTask.className)}`);
        if (dailyTask?.section) parts.push(`Section-${sanitize(dailyTask.section)}`);

        const timestamp = new Date().toISOString().split('T')[0];
        parts.push(timestamp);

        fileName = parts.filter(Boolean).join('_').substring(0, 100);
        fileName += `.${extension}`;
    }

    return fileName.replace(/\s+/g, '_').replace(/_{2,}/g, '_');
};

// Download helper function for blob data
const downloadBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

// Fetch file from URL with progress tracking
const fetchFileWithProgress = async (url, onProgress) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentLength = response.headers.get('Content-Length');
    const total = parseInt(contentLength, 10);
    let loaded = 0;

    const reader = response.body.getReader();
    const stream = new ReadableStream({
        start(controller) {
            function pump() {
                return reader.read().then(({done, value}) => {
                    if (done) {
                        controller.close();
                        return;
                    }
                    loaded += value.byteLength;
                    if (total && onProgress) {
                        onProgress(Math.round((loaded / total) * 100));
                    }
                    controller.enqueue(value);
                    return pump();
                });
            }
            return pump();
        }
    });

    return new Response(stream);
};

// Styled Components
const StyledContainer = styled(Container)(({theme}) => ({
    padding: theme.spacing(3),
    minHeight: '100vh',
    '& .MuiPaper-root': {
        borderRadius: 12,
        boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
    }
}));

const SearchContainer = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    backgroundColor: '#fff',
}));

const ActionButton = styled(Button)(({theme}) => ({
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
    '&:hover': {
        boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
    }
}));

const StyledDialog = styled(Dialog)(({theme}) => ({
    '& .MuiDialog-paper': {
        borderRadius: 16,
        padding: theme.spacing(2),
    }
}));

const DetailRow = styled(TableRow)(({theme}) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '& td': {
        padding: theme.spacing(2),
    }
}));

const DocumentViewer = styled(Box)(({theme}) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '70vh',
    maxHeight: '800px',
    minHeight: '500px',
    border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: theme.spacing(1),
    backgroundColor: '#f8f9fa',
    overflow: 'hidden',
}));

const DocumentControls = styled(Box)(({theme}) => ({
    position: 'sticky',
    top: 0,
    zIndex: 10,
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#ffffff',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
}));

const DocumentContent = styled(Box)(({theme}) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    overflow: 'auto',
    position: 'relative',
}));

const DownloadProgress = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: 'rgba(25, 118, 210, 0.1)',
    borderRadius: theme.spacing(1),
    minWidth: '200px',
}));

const DownloadStatus = styled(Card)(({theme}) => ({
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    minWidth: '300px',
    maxWidth: '400px',
    zIndex: theme.zIndex.snackbar,
    boxShadow: theme.shadows[8],
}));

// Helper component for document status chip
const DocumentStatusChip = ({ documentUrl }) => {
    if (!documentUrl) return null;

    const getFileInfo = (url) => {
        if (!url) return { type: 'unknown', icon: InsertDriveFileIcon, extension: '' };

        const extension = url.split('.').pop()?.toLowerCase() || '';
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        const pdfExtensions = ['pdf'];

        if (imageExtensions.includes(extension)) {
            return { type: 'image', icon: ImageIcon, extension };
        } else if (pdfExtensions.includes(extension)) {
            return { type: 'pdf', icon: PictureAsPdfIcon, extension };
        } else {
            return { type: 'document', icon: InsertDriveFileIcon, extension };
        }
    };

    const FileIcon = getFileInfo(documentUrl).icon;
    return (
        <Chip
            size="small"
            label="Available"
            color="success"
            icon={<FileIcon />}
        />
    );
};

const DailyTaskPage = ({items = []}) => {
    const [openForm, setOpenForm] = useState(false);
    const [selectedDailyTask, setSelectedDailyTask] = useState(null);
    const [openDetails, setOpenDetails] = useState(false);
    const [openDocumentViewer, setOpenDocumentViewer] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredItems, setFilteredItems] = useState(items);
    const [documentZoom, setDocumentZoom] = useState(1);
    const [documentError, setDocumentError] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadStatus, setDownloadStatus] = useState(null);

    const dispatch = useDispatch();
    const userData = useSelector(selectSchoolDetails);
    const schoolId = userData?.id;
    const session = userData?.session;
    const {dailyTaskList, loading, error} = useSelector((state) => state.dailyTask);

    useEffect(() => {
        if (schoolId && session) {
            dispatch(fetchDailyTask(schoolId, session));
        }
    }, [dispatch, schoolId, session]);

    const getFileInfo = (url) => {
        if (!url) return { type: 'unknown', icon: InsertDriveFileIcon, extension: '' };

        const extension = url.split('.').pop()?.toLowerCase() || '';
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        const pdfExtensions = ['pdf'];

        if (imageExtensions.includes(extension)) {
            return { type: 'image', icon: ImageIcon, extension };
        } else if (pdfExtensions.includes(extension)) {
            return { type: 'pdf', icon: PictureAsPdfIcon, extension };
        } else {
            return { type: 'document', icon: InsertDriveFileIcon, extension };
        }
    };

    // Enhanced download function with comprehensive error handling and progress tracking
    const handleDownloadDocument = async (documentData, originalFileName = '', dailyTask = null) => {
        if (!documentData) {
            setSnackbar({
                open: true,
                message: 'No document available to download',
                severity: 'error'
            });
            return;
        }

        setIsDownloading(true);
        setDownloadProgress({ progress: 0, message: 'Starting download...' });
        setDownloadStatus({
            status: 'downloading',
            fileName: originalFileName || 'document',
            progress: 0
        });

        try {
            let base64Data = documentData;
            let detectedType = { type: 'document', extension: 'bin', mimeType: 'application/octet-stream' };

            // Handle URL downloads
            if (documentData.startsWith('http://') || documentData.startsWith('https://')) {
                const urlParts = documentData.split('/').pop().split('.');
                if (urlParts.length > 1) {
                    const extension = urlParts.pop().toLowerCase();
                    const mimeTypes = {
                        'pdf': 'application/pdf',
                        'jpg': 'image/jpeg',
                        'jpeg': 'image/jpeg',
                        'png': 'image/png',
                        'gif': 'image/gif',
                        'bmp': 'image/bmp',
                        'webp': 'image/webp',
                        'svg': 'image/svg+xml',
                        'doc': 'application/msword',
                        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'xls': 'application/vnd.ms-excel',
                        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'ppt': 'application/vnd.ms-powerpoint',
                        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                        'txt': 'text/plain',
                        'json': 'application/json',
                        'xml': 'application/xml',
                        'zip': 'application/zip',
                        'rar': 'application/x-rar-compressed'
                    };

                    detectedType = {
                        extension,
                        mimeType: mimeTypes[extension] || 'application/octet-stream',
                        type: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension) ? 'image' :
                            extension === 'pdf' ? 'pdf' : 'document'
                    };
                }

                setDownloadProgress({ progress: 25, message: 'Fetching file...' });

                try {
                    // Try to fetch with progress if possible
                    const response = await fetchFileWithProgress(
                        documentData,
                        (progress) => {
                            setDownloadProgress({ progress: 25 + (progress * 0.5), message: `Downloading... ${progress}%` });
                            setDownloadStatus(prev => ({ ...prev, progress: 25 + (progress * 0.5) }));
                        }
                    );

                    setDownloadProgress({ progress: 75, message: 'Processing file...' });

                    const blob = await response.blob();
                    const fileName = generateFileName(originalFileName, dailyTask, detectedType.extension);

                    setDownloadProgress({ progress: 90, message: 'Preparing download...' });

                    downloadBlob(blob, fileName);

                    setDownloadProgress({ progress: 100, message: 'Download complete!' });
                    setDownloadStatus({
                        status: 'completed',
                        fileName,
                        progress: 100
                    });

                    setSnackbar({
                        open: true,
                        message: `Document "${fileName}" downloaded successfully!`,
                        severity: 'success'
                    });

                } catch (fetchError) {
                    console.warn('Direct fetch failed, trying fallback method:', fetchError);

                    // Fallback: direct link download
                    const fileName = generateFileName(originalFileName, dailyTask, detectedType.extension);
                    const link = document.createElement('a');
                    link.href = documentData;
                    link.download = fileName;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    setDownloadProgress({ progress: 100, message: 'Download initiated!' });
                    setDownloadStatus({
                        status: 'completed',
                        fileName,
                        progress: 100
                    });

                    setSnackbar({
                        open: true,
                        message: 'Document download initiated successfully!',
                        severity: 'success'
                    });
                }

                return;
            }

            // Handle base64 data
            setDownloadProgress({ progress: 25, message: 'Processing file data...' });

            if (documentData.startsWith('data:')) {
                const mimeMatch = documentData.match(/data:([^;]+);base64,/);
                if (mimeMatch) {
                    const mimeType = mimeMatch[1];
                    const extensionMap = {
                        'application/pdf': 'pdf',
                        'image/jpeg': 'jpg',
                        'image/jpg': 'jpg',
                        'image/png': 'png',
                        'image/gif': 'gif',
                        'image/bmp': 'bmp',
                        'image/webp': 'webp',
                        'image/svg+xml': 'svg',
                        'application/msword': 'doc',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
                        'application/vnd.ms-excel': 'xls',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
                        'application/vnd.ms-powerpoint': 'ppt',
                        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
                        'text/plain': 'txt',
                        'application/json': 'json',
                        'application/xml': 'xml',
                        'application/zip': 'zip'
                    };

                    detectedType = {
                        extension: extensionMap[mimeType] || 'bin',
                        mimeType: mimeType,
                        type: mimeType.startsWith('image/') ? 'image' :
                            mimeType === 'application/pdf' ? 'pdf' : 'document'
                    };
                }
                base64Data = documentData.split(',')[1];
            } else {
                // Detect file type from base64 content
                detectedType = detectFileTypeFromBase64(documentData);
                base64Data = documentData.replace(/^data:[^;]+;base64,/, '');
            }

            setDownloadProgress({ progress: 50, message: 'Converting file...' });

            // Convert base64 to blob
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            setDownloadProgress({ progress: 75, message: 'Preparing download...' });

            const blob = new Blob([bytes], { type: detectedType.mimeType });
            const fileName = generateFileName(originalFileName, dailyTask, detectedType.extension);

            setDownloadProgress({ progress: 90, message: 'Starting download...' });

            downloadBlob(blob, fileName);

            setDownloadProgress({ progress: 100, message: 'Download complete!' });
            setDownloadStatus({
                status: 'completed',
                fileName,
                progress: 100
            });

            setSnackbar({
                open: true,
                message: `Document "${fileName}" downloaded successfully!`,
                severity: 'success'
            });

        } catch (error) {
            console.error('Download failed:', error);

            setDownloadProgress(null);
            setDownloadStatus({
                status: 'error',
                fileName: originalFileName || 'document',
                error: error.message
            });

            setSnackbar({
                open: true,
                message: `Download failed: ${error.message}`,
                severity: 'error'
            });

            // Try fallback method for URLs
            if (documentData.startsWith('http://') || documentData.startsWith('https://')) {
                try {
                    window.open(documentData, '_blank');
                    setSnackbar({
                        open: true,
                        message: 'Document opened in new tab as fallback',
                        severity: 'info'
                    });
                } catch (fallbackError) {
                    console.error('Fallback also failed:', fallbackError);
                }
            }
        } finally {
            setIsDownloading(false);

            // Auto-hide download status after 3 seconds
            setTimeout(() => {
                setDownloadStatus(null);
                setDownloadProgress(null);
            }, 3000);
        }
    };

    const handleAddDailyTask = () => {
        setSelectedDailyTask(null);
        setOpenForm(true);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setFilteredItems(items);
    };

    const handleEditDailyTask = (dailyTask) => {
        console.log("On Edit Block");
        console.log(dailyTask);
        setSelectedDailyTask(dailyTask);
        setOpenForm(true);
    };

    const handleViewDailyTask = (dailyTask) => {
        setSelectedDailyTask(dailyTask);
        setOpenDetails(true);
    };

    const handleViewDocument = (dailyTask) => {
        setSelectedDailyTask(dailyTask);
        setDocumentZoom(1);
        setDocumentError(false);
        setOpenDocumentViewer(true);
    };

    const handleFormSubmit = (formData) => {
        if (formData.id) {
            dispatch(updateDailyTask(formData.id, formData));
        } else {
            dispatch(createDailyTask(formData));
        }
        setOpenForm(false);
    };

    const handleDeleteDailyTask = (id) => {
        dispatch(deleteDailyTask(id));
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        console.log("Search Query:", e.target.value);
    };

    // Enhanced Excel export with better error handling
    const handleDownloadExcel = () => {
        try {
            const filteredData = dailyTaskList.map(
                ({
                     docs,
                     identificationDocuments,
                     educationalCertificate,
                     professionalQualifications,
                     experienceCertificates,
                     bankAccount,
                     previousEmployer,
                     message,
                     ...rest
                 }) => ({
                    ...rest,
                    hasDocument: docs ? 'Yes' : 'No',
                    messagePreview: message ? message.substring(0, 100) : '',
                })
            );

            const worksheet = XLSX.utils.json_to_sheet(filteredData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "DailyTasks");
            const fileName = `DailyTasks_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(workbook, fileName);

            setSnackbar({
                open: true,
                message: `Excel file "${fileName}" exported successfully!`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Excel export failed:', error);
            setSnackbar({
                open: true,
                message: `Excel export failed: ${error.message}`,
                severity: 'error'
            });
        }
    };

    // Enhanced filtering
    const filteredDailyTaskList = Array.isArray(dailyTaskList)
        ? dailyTaskList.filter((dailyTask) => {
            const type = dailyTask.type?.toLowerCase() || "";
            const title = dailyTask.title?.toLowerCase() || "";
            const className = dailyTask.className?.toLowerCase() || "";
            const subject = dailyTask.subject?.toLowerCase() || "";
            const message = dailyTask.message?.toLowerCase() || "";
            const staffName = dailyTask.staffName?.toLowerCase() || "";
            const section = dailyTask.section?.toLowerCase() || "";
            const query = searchQuery.toLowerCase();

            return (
                type.includes(query) ||
                title.includes(query) ||
                className.includes(query) ||
                subject.includes(query) ||
                message.includes(query) ||
                staffName.includes(query) ||
                section.includes(query)
            );
        })
        : [];

    // Document viewer component
    const renderDocumentViewer = () => {
        if (!selectedDailyTask?.docs) {
            return (
                <DocumentViewer>
                    <DocumentControls>
                        <Chip
                            icon={<InsertDriveFileIcon />}
                            label="No Document"
                            color="default"
                            variant="outlined"
                        />
                    </DocumentControls>
                    <DocumentContent>
                        <Typography variant="h6" color="text.secondary" align="center">
                            No document available
                        </Typography>
                    </DocumentContent>
                </DocumentViewer>
            );
        }

        const fileInfo = getFileInfo(selectedDailyTask.docs);

        return (
            <DocumentViewer>
                <DocumentControls>
                    <Chip
                        icon={React.createElement(fileInfo.icon)}
                        label={`${fileInfo.type.toUpperCase()} File`}
                        color="primary"
                        variant="outlined"
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={() => setDocumentZoom(prev => Math.min(prev + 0.2, 3))}
                            disabled={documentZoom >= 3}
                            title="Zoom In"
                            size="small"
                        >
                            <ZoomInIcon />
                        </IconButton>
                        <Typography variant="body2" sx={{ minWidth: '60px', textAlign: 'center' }}>
                            {Math.round(documentZoom * 100)}%
                        </Typography>
                        <IconButton
                            onClick={() => setDocumentZoom(prev => Math.max(prev - 0.2, 0.5))}
                            disabled={documentZoom <= 0.5}
                            title="Zoom Out"
                            size="small"
                        >
                            <ZoomOutIcon />
                        </IconButton>
                        <IconButton
                            onClick={() => setDocumentZoom(1)}
                            title="Reset Zoom"
                            size="small"
                        >
                            <FullscreenIcon />
                        </IconButton>
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={isDownloading ? <CircularProgress size={16} /> : <GetAppIcon />}
                        onClick={() => handleDownloadDocument(
                            selectedDailyTask.docs,
                            `${selectedDailyTask.title || 'document'}_${selectedDailyTask.id || 'file'}.${fileInfo.extension}`,
                            selectedDailyTask
                        )}
                        disabled={isDownloading}
                    >
                        {isDownloading ? 'Downloading...' : 'Download'}
                    </Button>
                </DocumentControls>

                <DocumentContent>
                    {fileInfo.type === 'image' && (
                        <img
                            src={selectedDailyTask.docs}
                            alt="Document"
                            onError={() => setDocumentError(true)}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                transform: `scale(${documentZoom})`,
                                transformOrigin: 'center',
                                transition: 'transform 0.2s ease',
                                border: '1px solid rgba(0,0,0,0.1)',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            }}
                        />
                    )}

                    {fileInfo.type === 'pdf' && (
                        <iframe
                            src={selectedDailyTask.docs}
                            width="100%"
                            height="100%"
                            style={{
                                border: '1px solid rgba(0,0,0,0.1)',
                                borderRadius: '4px',
                                transform: `scale(${documentZoom})`,
                                transformOrigin: 'center top',
                                transition: 'transform 0.2s ease',
                                minHeight: '400px',
                            }}
                            title="PDF Document"
                            onError={() => setDocumentError(true)}
                        />
                    )}

                    {fileInfo.type === 'document' && (
                        <Card sx={{ maxWidth: 400, textAlign: 'center', boxShadow: 2 }}>
                            <CardContent>
                                {React.createElement(fileInfo.icon, {
                                    sx: { fontSize: 80, color: 'text.secondary', mb: 2 }
                                })}
                                <Typography variant="h6" gutterBottom>
                                    Document Preview Not Available
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    This file type cannot be previewed in the browser.
                                    Please download to view the content.
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    File Type: {fileInfo.extension.toUpperCase()}
                                </Typography>
                            </CardContent>
                            <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                                <Button
                                    variant="contained"
                                    startIcon={isDownloading ? <CircularProgress size={16} /> : <GetAppIcon />}
                                    onClick={() => handleDownloadDocument(
                                        selectedDailyTask.docs,
                                        `${selectedDailyTask.title || 'document'}_${selectedDailyTask.id || 'file'}`,
                                        selectedDailyTask
                                    )}
                                    disabled={isDownloading}
                                >
                                    {isDownloading ? 'Downloading...' : 'Download File'}
                                </Button>
                            </CardActions>
                        </Card>
                    )}

                    {documentError && (
                        <Alert severity="error" sx={{ width: '90%', maxWidth: '500px' }}>
                            <Typography variant="body2" gutterBottom>
                                Failed to load document. This might be due to:
                            </Typography>
                            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                <li>Unsupported file format for preview</li>
                                <li>Network connectivity issues</li>
                                <li>File corruption or access restrictions</li>
                            </ul>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<GetAppIcon />}
                                onClick={() => handleDownloadDocument(
                                    selectedDailyTask.docs,
                                    `${selectedDailyTask.title || 'document'}_${selectedDailyTask.id || 'file'}`,
                                    selectedDailyTask
                                )}
                                sx={{ mt: 1 }}
                            >
                                Try Download Instead
                            </Button>
                        </Alert>
                    )}
                </DocumentContent>
            </DocumentViewer>
        );
    };

    // Close snackbar
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    // Close download status
    const handleCloseDownloadStatus = () => {
        setDownloadStatus(null);
        setDownloadProgress(null);
    };

    return (
        <StyledContainer maxWidth="lg">
            <Box sx={{minHeight: '200px'}}>
                {loading ? (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        <Skeleton variant="rectangular" width="100%" height={60} sx={{borderRadius: 2}}/>
                        <Skeleton variant="rectangular" width="100%" height={60} sx={{borderRadius: 2}}/>
                        <Skeleton variant="rectangular" width="100%" height={400} sx={{borderRadius: 2}}/>
                    </Box>
                ) : error ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="60vh"
                    >
                        <Alert
                            severity="error"
                            sx={{
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                        >
                            {error}
                        </Alert>
                    </Box>
                ) : (
                    <>
                        <SearchContainer elevation={0}>
                            <TextField
                                label="Search by Subject, Type, Title, Message, Staff, or Class"
                                variant="outlined"
                                value={searchQuery}
                                onChange={handleSearch}
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action"/>
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="clear"
                                                onClick={handleClearSearch}
                                                size="small"
                                            >
                                                <ClearIcon/>
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: {borderRadius: 2}
                                }}
                            />
                            <ActionButton
                                variant="contained"
                                color="primary"
                                onClick={handleAddDailyTask}
                                startIcon={<AddCircleOutlineIcon/>}
                            >
                                Add Assignment
                            </ActionButton>
                            <Tooltip title="Download all Daily Task details as Excel">
                                <ActionButton
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleDownloadExcel}
                                    startIcon={<FileDownloadIcon/>}
                                >
                                    Export Excel
                                </ActionButton>
                            </Tooltip>
                        </SearchContainer>

                        <Paper elevation={0} sx={{p: 2, borderRadius: 3}}>
                            <DailyTaskList
                                dailyTaskList={filteredDailyTaskList}
                                onEdit={handleEditDailyTask}
                                onDelete={handleDeleteDailyTask}
                                onView={handleViewDailyTask}
                                onViewDocument={handleViewDocument}
                                onDownloadDocument={handleDownloadDocument}
                            />
                        </Paper>

                        {/* Form Dialog */}
                        <StyledDialog
                            open={openForm}
                            onClose={() => setOpenForm(false)}
                            fullWidth
                            maxWidth="md"
                        >
                            <DialogTitle sx={{
                                pb: 2,
                                borderBottom: '1px solid rgba(0,0,0,0.1)',
                                typography: 'h5',
                                fontWeight: 600
                            }}>
                                {selectedDailyTask ? "Edit Daily Task" : "Add Daily Task"}
                            </DialogTitle>
                            <DialogContent sx={{mt: 2}}>
                                <DailyTaskForm
                                    dailyTask={selectedDailyTask}
                                    onSubmit={handleFormSubmit}
                                    onCancel={() => setOpenForm(false)}
                                />
                            </DialogContent>
                        </StyledDialog>

                        {/* Details Dialog */}
                        <StyledDialog
                            open={openDetails}
                            onClose={() => setOpenDetails(false)}
                            fullWidth
                            maxWidth="md"
                        >
                            <DialogTitle sx={{
                                textAlign: "center",
                                position: "relative",
                                pb: 2,
                                borderBottom: '1px solid rgba(0,0,0,0.1)',
                                typography: 'h5',
                                fontWeight: 600
                            }}>
                                Daily Activities Details
                                <IconButton
                                    aria-label="close"
                                    onClick={() => setOpenDetails(false)}
                                    sx={{
                                        position: "absolute",
                                        right: 8,
                                        top: 8,
                                    }}
                                >
                                    <CloseIcon/>
                                </IconButton>
                            </DialogTitle>
                            <DialogContent sx={{mt: 2}}>
                                {selectedDailyTask ? (
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        textAlign: "center",
                                        minHeight: "200px",
                                    }}>
                                        <Table>
                                            <TableBody>
                                                {[
                                                    {label: "Subject", value: selectedDailyTask.subject},
                                                    {label: "Type", value: selectedDailyTask.type},
                                                    {label: "Title", value: selectedDailyTask.title},
                                                    {
                                                        label: "Document",
                                                        value: selectedDailyTask.docs ? (
                                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                                <DocumentStatusChip documentUrl={selectedDailyTask.docs} />
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<VisibilityIcon />}
                                                                    onClick={() => handleViewDocument(selectedDailyTask)}
                                                                >
                                                                    View
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    startIcon={isDownloading ? <CircularProgress size={16} /> : <GetAppIcon />}
                                                                    onClick={() => handleDownloadDocument(
                                                                        selectedDailyTask.docs,
                                                                        `${selectedDailyTask.title}_${selectedDailyTask.id}`,
                                                                        selectedDailyTask
                                                                    )}
                                                                    disabled={isDownloading}
                                                                >
                                                                    {isDownloading ? 'Downloading...' : 'Download'}
                                                                </Button>
                                                            </Box>
                                                        ) : (
                                                            <Typography color="text.secondary">No document uploaded</Typography>
                                                        ),
                                                    },
                                                    {label: "Message", value: selectedDailyTask.message},
                                                    {label: "Staff Name", value: selectedDailyTask.staffName},
                                                    {label: "Class Name", value: selectedDailyTask.className},
                                                    {label: "Section", value: selectedDailyTask.section},
                                                    {label: "Date of Creation", value: selectedDailyTask.createdDate},
                                                ].map((item) => (
                                                    <DetailRow key={item.label}>
                                                        <TableCell sx={{fontWeight: 600, width: '30%'}}>
                                                            {item.label}
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.value || (
                                                                <Typography color="text.secondary">Not available</Typography>
                                                            )}
                                                        </TableCell>
                                                    </DetailRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                ) : (
                                    <Skeleton variant="rectangular" width="100%" height={200} sx={{borderRadius: 2}}/>
                                )}
                            </DialogContent>
                        </StyledDialog>

                        {/* Document Viewer Dialog */}
                        <StyledDialog
                            open={openDocumentViewer}
                            onClose={() => setOpenDocumentViewer(false)}
                            fullWidth
                            maxWidth="lg"
                        >
                            <DialogTitle sx={{
                                textAlign: "center",
                                position: "relative",
                                pb: 2,
                                borderBottom: '1px solid rgba(0,0,0,0.1)',
                                typography: 'h5',
                                fontWeight: 600
                            }}>
                                Document Viewer - {selectedDailyTask?.title || 'Document'}
                                <IconButton
                                    aria-label="close"
                                    onClick={() => setOpenDocumentViewer(false)}
                                    sx={{
                                        position: "absolute",
                                        right: 8,
                                        top: 8,
                                    }}
                                >
                                    <CloseIcon/>
                                </IconButton>
                            </DialogTitle>
                            <DialogContent sx={{ mt: 2, p: 0, height: '80vh', maxHeight: '800px' }}>
                                {renderDocumentViewer()}
                            </DialogContent>
                        </StyledDialog>

                        {/* Download Progress Snackbar */}
                        <Snackbar
                            open={snackbar.open}
                            autoHideDuration={6000}
                            onClose={handleCloseSnackbar}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        >
                            <Alert
                                onClose={handleCloseSnackbar}
                                severity={snackbar.severity}
                                variant="filled"
                                sx={{ width: '100%' }}
                            >
                                {snackbar.message}
                            </Alert>
                        </Snackbar>

                        {/* Download Status Card */}
                        {downloadStatus && (
                            <DownloadStatus>
                                <CardContent sx={{ pb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {downloadStatus.status === 'downloading' && <CloudDownloadIcon color="primary" />}
                                            {downloadStatus.status === 'completed' && <CheckCircleIcon color="success" />}
                                            {downloadStatus.status === 'error' && <ErrorIcon color="error" />}
                                            {downloadStatus.status === 'downloading' && 'Downloading...'}
                                            {downloadStatus.status === 'completed' && 'Download Complete'}
                                            {downloadStatus.status === 'error' && 'Download Failed'}
                                        </Typography>
                                        <IconButton size="small" onClick={handleCloseDownloadStatus}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {downloadStatus.fileName}
                                    </Typography>

                                    {downloadStatus.status === 'downloading' && (
                                        <Box sx={{ mt: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={downloadStatus.progress}
                                                sx={{ height: 6, borderRadius: 3 }}
                                            />
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                {Math.round(downloadStatus.progress)}%
                                            </Typography>
                                        </Box>
                                    )}

                                    {downloadStatus.status === 'error' && (
                                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                            Error: {downloadStatus.error}
                                        </Typography>
                                    )}
                                </CardContent>
                            </DownloadStatus>
                        )}

                        {/* Global Download Progress Indicator */}
                        {downloadProgress && (
                            <Box
                                sx={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    zIndex: (theme) => theme.zIndex.modal + 1,
                                }}
                            >
                                <LinearProgress
                                    variant="determinate"
                                    value={downloadProgress.progress}
                                    sx={{
                                        height: 4,
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: downloadProgress.progress === 100 ? '#4caf50' : '#2196f3'
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </StyledContainer>
    );
};

export default DailyTaskPage;